import { SERVICE_MODES, type ServiceMode } from '../enums';
import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';

export interface DailyServiceFrequencyFilters {
  serviceMode?: ServiceMode[];
  isFree?: boolean;
}

/** One sparse row per provider and Bangkok service business date. */
export interface DailyServiceFrequencyRow {
  providerId: string;
  providerName: string;
  businessDate: string;
  dateBasis: 'SERVICE_DATE';
  scheduledDateTripCount: number;
  createdDateFallbackTripCount: number;
  requestsCount: number;
  acceptedTripCount: number;
  completedTripCount: number;
  cancelledTripCount: number;
  missedTripCount: number;
  cancelledByPassengerCount: number;
  cancelledByAgentCount: number;
  cancelledByOrgUserCount: number;
  cancelledBySystemCount: number;
  cancelledByOtherCount: number;
  cancelledByUnknownCount: number;
  offersCount: number;
  acceptedOfferCount: number;
  rejectedOfferCount: number;
  expiredOfferCount: number;
  supersededOfferCount: number;
  cancelledOfferCount: number;
  pendingOfferCount: number;
  currency: 'THB';
  fareTotalSum: number;
  billedTotalSum: number;
  receivedTotalSum: number;
  outstandingTotalSum: number;
  freeTripCount: number;
  financeReviewCount: number;
  missingFareCount: number;
}

export const dailyServiceFrequencyDateBasis = 'SERVICE_DATE' as const;
export const dailyServiceFrequencySpecificFilters = [
  { id: 'serviceMode', type: 'multi', values: SERVICE_MODES },
  { id: 'isFree', type: 'boolean' },
] as const satisfies readonly ReportSpecificFilter[];

function column(
  id: string,
  type: ReportTableColumn['type'],
  source: string,
  emptyMeaning = 'zero',
  defaultVisible = false,
): ReportTableColumn {
  return {
    id,
    labelKey: `dailyFrequencyColumn_${id}`,
    type,
    ...(type === 'number' ? { unitKey: 'unit_count' } : {}),
    source: `dailyFrequencySource_${source}`,
    emptyMeaning: `dailyFrequencyEmpty_${emptyMeaning}`,
    defaultVisible,
    ...(source !== 'provider' && id !== 'currency' ? { meaningKey: `dailyFrequencyMeaning_${source}` } : {}),
    ...(id === 'business_date' ? { sortable: true, sortKey: 'businessDate' } : {}),
  };
}

export const dailyServiceFrequencyColumns = [
  column('provider_id', 'text', 'provider', 'present'),
  column('provider_name', 'text', 'provider', 'present', true),
  column('business_date', 'date', 'serviceDate', 'present', true),
  column('date_basis', 'text', 'serviceDate', 'present'),
  column('scheduled_date_trip_count', 'number', 'scheduled'),
  column('created_date_fallback_trip_count', 'number', 'fallback'),
  column('requests_count', 'number', 'cohort', 'zero', true),
  column('accepted_trip_count', 'number', 'accepted', 'zero', true),
  column('completed_trip_count', 'number', 'completed', 'zero', true),
  column('cancelled_trip_count', 'number', 'terminal', 'zero', true),
  column('missed_trip_count', 'number', 'terminal', 'zero', true),
  column('cancelled_by_passenger_count', 'number', 'cancellation'),
  column('cancelled_by_agent_count', 'number', 'cancellation'),
  column('cancelled_by_org_user_count', 'number', 'cancellation'),
  column('cancelled_by_system_count', 'number', 'cancellation'),
  column('cancelled_by_other_count', 'number', 'cancellationOther'),
  column('cancelled_by_unknown_count', 'number', 'cancellationUnknown'),
  column('offers_count', 'number', 'offers', 'zero', true),
  column('accepted_offer_count', 'number', 'offers'),
  column('rejected_offer_count', 'number', 'offers'),
  column('expired_offer_count', 'number', 'offers'),
  column('superseded_offer_count', 'number', 'offers'),
  column('cancelled_offer_count', 'number', 'offers'),
  column('pending_offer_count', 'number', 'offers'),
  column('currency', 'text', 'finance', 'present'),
  column('fare_total_sum', 'money', 'fare', 'missingFare', true),
  column('billed_total_sum', 'money', 'finance', 'zero', true),
  column('received_total_sum', 'money', 'receipts', 'zero', true),
  column('outstanding_total_sum', 'money', 'finance', 'zero', true),
  column('free_trip_count', 'number', 'free'),
  column('finance_review_count', 'number', 'review', 'zero', true),
  column('missing_fare_count', 'number', 'missingFare', 'zero', true),
] as const satisfies readonly ReportTableColumn[];

export const dailyServiceFrequencyDefaultColumns = dailyServiceFrequencyColumns
  .filter((item) => item.defaultVisible)
  .map((item) => item.id);

export const dailyServiceFrequencyAdditiveFields = [
  'scheduledDateTripCount',
  'createdDateFallbackTripCount',
  'requestsCount',
  'acceptedTripCount',
  'completedTripCount',
  'cancelledTripCount',
  'missedTripCount',
  'cancelledByPassengerCount',
  'cancelledByAgentCount',
  'cancelledByOrgUserCount',
  'cancelledBySystemCount',
  'cancelledByOtherCount',
  'cancelledByUnknownCount',
  'offersCount',
  'acceptedOfferCount',
  'rejectedOfferCount',
  'expiredOfferCount',
  'supersededOfferCount',
  'cancelledOfferCount',
  'pendingOfferCount',
  'fareTotalSum',
  'billedTotalSum',
  'receivedTotalSum',
  'outstandingTotalSum',
  'freeTripCount',
  'financeReviewCount',
  'missingFareCount',
] as const satisfies readonly (keyof DailyServiceFrequencyRow)[];

export const dailyServiceFrequencySummary = [
  { field: 'totalRows', labelKey: 'dailyFrequencySummary_totalRows', unit: 'count' },
  { field: 'tripCount', labelKey: 'dailyFrequencySummary_tripCount', unit: 'count' },
  ...dailyServiceFrequencyAdditiveFields
    .filter((field) => field !== 'requestsCount')
    .map((field) => ({
      field,
      labelKey: `dailyFrequencySummary_${field}`,
      unit: field.endsWith('Sum') ? ('money' as const) : ('count' as const),
    })),
  {
    field: 'withoutAcceptanceEvidenceTripCount',
    labelKey: 'dailyFrequencySummary_withoutAcceptanceEvidenceTripCount',
    unit: 'count',
  },
  {
    field: 'completedStateWithoutEventTripCount',
    labelKey: 'dailyFrequencySummary_completedStateWithoutEventTripCount',
    unit: 'count',
  },
  {
    field: 'cancelledStateWithoutEventTripCount',
    labelKey: 'dailyFrequencySummary_cancelledStateWithoutEventTripCount',
    unit: 'count',
  },
] as const;
